/**
 * LoadingSpinner Component
 *
 * Accessible loading spinner for button loading states and other loading indicators.
 * Sizes match button sizes for visual consistency.
 *
 * @example
 * ```tsx
 * import { LoadingSpinner } from '@/ui';
 *
 * // In a button
 * {isLoading && <LoadingSpinner size="md" />}
 *
 * // Standalone
 * <LoadingSpinner size="lg" />
 * ```
 */

import * as React from 'react';
import {cn} from '@/shared/utils/utils';

// =============================================================================
// TYPES
// =============================================================================

export type LoadingSpinnerSize = 'sm' | 'md' | 'lg' | 'icon' | 'default' | 'xl' | 'caption';

interface LoadingSpinnerProps {
  /**
   * Size of the spinner
   * @default 'md'
   */
  size?: LoadingSpinnerSize;
  /**
   * Additional className
   */
  className?: string;
  /**
   * ARIA label for screen readers
   * @default 'Loading, please wait'
   */
  'aria-label'?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * LoadingSpinner - Accessible loading spinner
 *
 * Use this component for loading states in buttons, cards, or any UI element.
 * The spinner automatically sizes to match button sizes for consistency.
 */
export function LoadingSpinner({
  size = 'md',
  className,
  'aria-label': ariaLabel = 'Loading, please wait',
}: LoadingSpinnerProps): React.ReactElement {
  const sizeClasses: Record<LoadingSpinnerSize, string> = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
    icon: 'h-4 w-4',
    default: 'h-4 w-4',
    xl: 'h-5 w-5',
    caption: 'h-3 w-3',
  };

  return (
    <svg
      className={cn('motion-safe:animate-spin', sizeClasses[size], className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-label={ariaLabel}
      aria-hidden={!ariaLabel}
      role="status"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

LoadingSpinner.displayName = 'LoadingSpinner';
